import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2, Link as LinkIcon, X } from "lucide-react";
import api, { API, formatApiError } from "../lib/api";

/**
 * Image uploader with URL fallback.
 * Props:
 *  - value: current image URL (string) — may be a full URL or /api/files/:id
 *  - onChange: (newUrl: string) => void
 *  - testid
 */
export default function ImageUpload({ value, onChange, testid = "image-upload" }) {
  const [uploading, setUploading] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const [manual, setManual] = useState(value || "");
  const [meta, setMeta] = useState(null);
  const fileInput = useRef(null);

  const resolvedSrc = value
    ? value.startsWith("/api/")
      ? `${process.env.REACT_APP_BACKEND_URL}${value}`
      : value
    : null;

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please select an image"); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Max 8MB"); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      setMeta(data);
      const savedMsg = data.saved_kb > 0 ? ` · saved ${data.saved_kb} KB as WebP` : " · stored as WebP";
      toast.success(`Uploaded${savedMsg}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); }
  };

  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="flex items-start gap-3">
        <div className="w-24 h-24 rounded-xl border border-zinc-200 bg-zinc-50 flex items-center justify-center overflow-hidden flex-shrink-0">
          {resolvedSrc ? (
            <img src={resolvedSrc} alt="" className="w-full h-full object-cover" />
          ) : (
            <span className="text-[10px] text-zinc-400">No image</span>
          )}
        </div>
        <div className="flex-1 flex flex-col gap-2">
          <div className="flex gap-2">
            <button type="button" disabled={uploading}
                    onClick={() => fileInput.current?.click()}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full bg-zinc-900 text-white hover:bg-zinc-700 disabled:opacity-50"
                    data-testid={`${testid}-btn`}>
              {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
              {uploading ? "Uploading…" : "Upload image"}
            </button>
            <button type="button" onClick={() => setManualMode(!manualMode)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full border border-zinc-200 hover:bg-zinc-50"
                    data-testid={`${testid}-url-toggle`}>
              <LinkIcon size={12} /> Use URL
            </button>
            {value && (
              <button type="button" onClick={() => onChange("")} className="p-1.5 text-zinc-400 hover:text-red-500" title="Remove">
                <X size={14} />
              </button>
            )}
          </div>
          {manualMode ? (
            <div className="flex gap-2">
              <input type="url" value={manual} onChange={(e) => setManual(e.target.value)} placeholder="https://..."
                     className="flex-1 border border-zinc-200 rounded-lg px-2 py-1 text-xs" data-testid={`${testid}-url-input`} />
              <button type="button" onClick={() => { onChange(manual); setManualMode(false); }}
                      className="px-3 py-1 text-xs font-semibold bg-zinc-900 text-white rounded">Set</button>
            </div>
          ) : (
            <div className="text-[10px] text-zinc-500 truncate max-w-md" title={value || ""}>
              {meta
                ? `WebP · ${meta.dimensions?.[0]}×${meta.dimensions?.[1]} · ${Math.round(meta.size / 1024)} KB${meta.saved_kb > 0 ? ` (saved ${meta.saved_kb} KB)` : ""}`
                : value ? value.slice(0, 80) : "PNG, JPG, WebP up to 8MB · auto-converts to WebP"}
            </div>
          )}
          <input ref={fileInput} type="file" accept="image/*" className="hidden"
                 onChange={(e) => handleFile(e.target.files?.[0])} data-testid={`${testid}-input`} />
        </div>
      </div>
    </div>
  );
}

export function resolveImageUrl(url) {
  if (!url) return null;
  if (url.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return url;
}
